import { useMemo } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Skeleton } from '@/components/ui/skeleton';
import { PricelistCsvGrid } from './PricelistCsvGrid';
import { buildCsvDrafts } from './pricelistCsvEditState';
import type { PricelistSearchView } from './usePricelistSearch';

/**
 * The matching rows of one table, as the same spreadsheet its rows are shown in
 * everywhere else.
 *
 * A grid rather than a plain table so ranges copy out as cells — into Excel next
 * to catalog data, which is what the comparison panel exists for. Row headers
 * carry each row's real position in the table, not its position in the result
 * set, so a match 8,000 rows down says so.
 *
 * One table at a time: a grid has one set of columns, and tables in a pricelist
 * don't share headers. The tab strip already picks the table and carries each
 * one's match count, so the other tables' hits are a tab away.
 *
 * Read-only, including on the editing surface. The rows here are copies —
 * `buildCsvDrafts` clones them — so an edit typed into a result would be
 * discarded silently. Clearing the search returns to the editable grid.
 */
export function PricelistSearchResults({
  view,
  tableIndex,
  query,
  maxGridHeight,
}: {
  view: PricelistSearchView;
  /** Which table's matches to show — the open tab. */
  tableIndex: number;
  /** Only to reload the grid when the search changes; not displayed. */
  query: string;
  /** Caps the results grid the same way the table it replaces is capped. */
  maxGridHeight?: number;
}) {
  const gt = useGT();
  const { groups, totalHits, truncated, status } = view;
  const group = groups.find((one) => one.tableIndex === tableIndex);
  const hits = group?.hits ?? [];

  const draft = useMemo(() => {
    if (!group) return undefined;
    return buildCsvDrafts([
      { headers: group.headers, rows: group.hits.map((hit) => hit.row) },
    ])[0];
  }, [group]);

  if (status === 'error') {
    return (
      <div className="rounded-md border px-4 py-3 text-sm text-destructive">
        {gt('Failed to search this pricelist.', {
          context: 'error state when the in-pricelist search request fails',
        })}
      </div>
    );
  }

  if (!draft || hits.length === 0) {
    if (status === 'searching') {
      return (
        <div className="flex flex-col gap-2 rounded-md border px-4 py-3">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      );
    }
    return (
      <div className="rounded-md border px-4 py-3 text-sm text-muted-foreground">
        {totalHits > 0 ? (
          // Saying "no matches" flat would be a lie while other tabs hold some.
          <T context="empty state when a pricelist search matched other tables but not the open one">
            No matches in this table. <Var>{totalHits.toLocaleString()}</Var> in
            the others — check their tabs.
          </T>
        ) : (
          <T context="empty state when an in-pricelist search matches no rows">
            No rows match that search.
          </T>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">
        {truncated ? (
          <T context="count of pricelist search matches when the search stopped at its limit">
            Showing the first <Var>{totalHits.toLocaleString()}</Var> matches.
            Narrow the search to see fewer.
          </T>
        ) : (
          <T context="count of pricelist search matches">
            <Var>{totalHits.toLocaleString()}</Var> matching rows
          </T>
        )}
      </span>
      <PricelistCsvGrid
        // Keyed on the query so a new search can't leave the previous one's rows
        // on screen; `dataVersion` reloads it as streamed hits arrive.
        key={`${query}:${tableIndex}`}
        draft={draft}
        dataVersion={hits.length}
        rowLabels={(index) =>
          ((hits[index]?.rowIndex ?? index) + 1).toLocaleString()
        }
        readOnly
        maxHeight={maxGridHeight}
      />
    </div>
  );
}
